import { Component, inject, Input, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  Conversation,
  ConversationChannel,
  MessageType,
  ParticipantType,
  EvaluationSentiment,
} from '@vendasta/conversation';
import { ChatContainerComponent, ChatMessageStatus } from '@vendasta/galaxy/chat';
import { defaultBackgroundColor } from '@vendasta/galaxy/avatar';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, combineLatest, firstValueFrom, map } from 'rxjs';
import { DEFAULT_OPENAI_BOT_NAME } from '../../../../../../core/src/lib/inbox.constants';
import {
  ConversationEvent,
  ConversationMessage,
} from '../../../../../../core/src/lib/interface/conversation.interface';
import { MessageGroup, MessageGroupable } from '../../../../../../core/src/lib/types';
import { ConversationService } from '../../../../../../core/src/lib/conversation.service';
import { DEFAULT_HOUR_GROUP_DURATION, DEFAULT_MESSAGE_GROUPING_DURATION } from '../../../../constants';
import { MediaDialogComponent } from './media-dialog.component';
import { DialogData, ResponseEvaluationDialogComponent } from './response-evaluation-dialog.component';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';

export interface ResponseEvaluation {
  helpful: boolean;
  comment?: string;
}

@Component({
  selector: 'inbox-messages',
  templateUrl: './inbox-messages.component.html',
  styleUrls: ['./inbox-messages.component.scss'],
})
export class InboxMessagesComponent {
  readonly defaultBackgroundColor = defaultBackgroundColor;
  readonly mediaWithErrorsMessageIDs = signal<string[]>([]);
  readonly isConversationEvent = isConversationEvent;
  readonly RECEIVED = 'received';
  readonly MessageType = MessageType;
  readonly OPENAI_BOT_NAME = DEFAULT_OPENAI_BOT_NAME;
  readonly OPENAI_BOT_TYPE = ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT;
  readonly OPENAI_BOT_SVG_ICON = 'galaxy-ai-icon-with-background';
  readonly OPENAI_BOT_BACKGROUND_COLOR = '#f6f6f6';
  readonly acceptedMimeType = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'video/mp4',
    'video/3gpp',
    'video/WebM',
    'video/ogg',
    'audio/ogg',
    'audio/acc',
    'audio/wav',
    'audio/mp3',
    'audio/mp4',
    'audio/mpeg',
    'audio/webm',
  ];
  private readonly messages$$ = new BehaviorSubject<ConversationMessage[]>([]);
  private readonly messages$ = this.messages$$.asObservable();
  private readonly conversationDetail$$ = new BehaviorSubject<Conversation>(null);

  protected readonly conversationId$ = this.conversationDetail$$.pipe(
    map((conversation) => conversation?.conversationId || ''),
  );

  @Input() set messages(value: ConversationMessage[]) {
    this.mediaWithErrorsMessageIDs.set([]);
    this.messages$$.next(value);
  }

  private readonly events$$ = new BehaviorSubject<ConversationEvent[]>([]);
  @Input() set events(value: ConversationEvent[]) {
    this.events$$.next(value);
  }

  @Input() set conversation(value: Conversation) {
    this.conversationDetail$$.next(value);
  }

  @ViewChild('chatContainer') chatContainer: ChatContainerComponent | undefined;

  readonly groupedMessages$ = combineLatest([this.messages$, this.events$$]).pipe(
    map(([messages, events]) => groupMessages(messages, events)),
    map((messages) => messages?.slice().reverse()), // reverse messages to render in glxy-chat-container
  );

  readonly loadingOpenAIReply = toSignal(
    combineLatest([this.conversationDetail$$, this.messages$]).pipe(
      map(([conversation, messages]) => {
        if (conversation?.channel !== ConversationChannel.CONVERSATION_CHANNEL_OPENAI) {
          return false;
        }

        const latestMessage = messages?.[0];
        if (!latestMessage) {
          return false;
        }

        // NOTE: This is always assuming that Open AI will have a response for every message
        // However, we will always aim for a chat response from the backend if openai fails
        // We're making a tradeoff here until we have something in the backend that we can use to handle this
        if (latestMessage?.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_IAM_USER) {
          return true;
        }
        return false;
      }),
    ),
  );
  private readonly conversationService = inject(ConversationService);
  readonly aiIconService = inject(GalaxyAiIconService); // used to load the ai avatar svg
  constructor(
    private readonly analytics: ProductAnalyticsService,
    private readonly dialog: MatDialog,
  ) {}

  openInDialog(url: string, contentType: string) {
    this.analytics.trackEvent('inbox', 'openMediaDialog', 'view');
    this.dialog.open(MediaDialogComponent, {
      data: { mediaUrl: url, mediaContentType: contentType },
      height: 'auto',
      maxWidth: '860px',
    });
  }

  trackById(_: number, item: MessageGroupable): string {
    return isConversationEvent(item) ? item.eventId : item?.id;
  }

  addMessageIdToMediaErrors(messageID: string) {
    this.mediaWithErrorsMessageIDs.update((errors) => [...errors, messageID]);
  }

  async openEvaluateModal(helpful: boolean, message: ConversationMessage): Promise<void> {
    const data: DialogData = {
      helpful: helpful,
    };
    const modalRef$ = this.dialog.open(ResponseEvaluationDialogComponent, {
      data: data,
      width: '660px',
    });
    const submission = await firstValueFrom(modalRef$.afterClosed());
    if (submission) {
      const sentiment = helpful
        ? EvaluationSentiment.EVALUATION_SENTIMENT_POSITIVE
        : EvaluationSentiment.EVALUATION_SENTIMENT_NEGATIVE;
      await this.conversationService.evaluateResponse(<string>message.messageId, submission?.comment, sentiment);
    }
  }

  mapMessageStatus(message: ConversationMessage): ChatMessageStatus {
    return this.conversationService.mapStatusForMessage(message);
  }
}

export function groupMessages(messages?: ConversationMessage[], events?: ConversationEvent[]): MessageGroupable[] {
  const allItems = [...(messages || []), ...(events || [])].filter(Boolean).sort((a, b) => {
    const aDate = isConversationEvent(a) ? a.happenedAt.toDate().getTime() : a.created.getTime();
    const bDate = isConversationEvent(b) ? b.happenedAt.toDate().getTime() : b.created.getTime();
    return aDate - bDate;
  });

  return allItems.reduce((list, item) => {
    // don't group events and system messages
    if (isConversationEvent(item) || isSystemMessage(item)) {
      return [...list, item];
    }

    // if possible, add the message to the previous message group
    const previousGroup = list[list.length - 1];
    if (canAddMessageToPreviousGroup(item, previousGroup)) {
      previousGroup.addMessage(item);
      return list;
    }

    // othwerwise, create a new message group and add the item to it
    const newGroup = createNewGroupWithMessage(item, previousGroup?.lastMessage);
    return [...list, newGroup];
  }, []);
}

function isConversationEvent(item: MessageGroupable): item is ConversationEvent {
  return 'happenedAt' in item;
}

function isSystemMessage(item: ConversationMessage | ConversationEvent): boolean {
  return 'type' in item && item.type === MessageType.MESSAGE_TYPE_SYSTEM;
}

function canAddMessageToPreviousGroup(message: ConversationMessage, previousGroup: MessageGroupable): boolean {
  if (previousGroup instanceof MessageGroup) {
    const lastMessage = previousGroup.lastMessage;
    const isSameSender = message.sender?.participantId === lastMessage?.sender?.participantId;
    const isSameType = message.type === lastMessage?.type;
    const isSameChannel = message.channel === lastMessage?.channel;
    const timeElapsed = message.created?.getTime() - lastMessage?.created?.getTime();
    const isWithinDuration = timeElapsed < DEFAULT_MESSAGE_GROUPING_DURATION;
    return isSameSender && isSameType && isSameChannel && isWithinDuration;
  }
  return false;
}

function createNewGroupWithMessage(
  message: ConversationMessage,
  previousMessage: ConversationMessage | undefined,
): MessageGroup {
  // if enough time has passed since the previous message, set the createdAt
  // date of the new message group so a date divider will be shown in the
  // conversation stream
  const previousDate = previousMessage?.created;
  const messageDate = message.created;
  const enoughTimeElapsed = messageDate.getTime() - previousDate?.getTime() > DEFAULT_HOUR_GROUP_DURATION;
  const addTimeDivider = !previousDate || enoughTimeElapsed;
  const createdAt = addTimeDivider ? messageDate : null;

  const { id, sender, channel, type } = message;
  const newGroup = new MessageGroup(id, sender, type, channel, createdAt);
  newGroup.addMessage(message);
  return newGroup;
}
